import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Copy, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import api from "@/lib/api";
import { goldBtnSm } from "@/lib/constants";

export default function WebhookSecrets() {
  const [secrets, setSecrets] = useState([]);
  const [reveal, setReveal] = useState(null);

  const load = () => api.get("/admin/webhook-secrets").then((r) => setSecrets(r.data));
  useEffect(() => { load(); }, []);

  const rotate = async (name) => {
    try { const { data } = await api.post("/admin/webhook-secrets/generate", { name, length: 32 }); setReveal(data); load(); }
    catch (e) { toast.error("Failed to generate"); }
  };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied to clipboard"); };

  return (
    <PortalLayout title="Webhook Secrets">
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Raw secret values are never readable here. Only SHA-256 fingerprints (first 12 hex characters) and lengths are shown.
        Rotating a secret reveals the new value exactly once for counterparty configuration.
      </p>
      <div className="space-y-4">
        {secrets.map((s) => (
          <GlassCard key={s.name} className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <KeyRound size={18} className="text-[hsl(var(--pdg-gold))]" />
                <div>
                  <div className="text-sm text-foreground pdg-mono">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">Fingerprint <span className="pdg-mono text-[hsl(var(--pdg-gold))]" data-testid="admin-webhook-secret-value">{s.fingerprint}</span> &middot; length {s.length} &middot; rotated {s.rotated_at ? new Date(s.rotated_at).toLocaleDateString() : "—"}</div>
                </div>
              </div>
              <button onClick={() => rotate(s.name)} data-testid={`webhook-rotate-${s.name}`} className={goldBtnSm}><RefreshCw size={13} /> Rotate</button>
            </div>
          </GlassCard>
        ))}
      </div>

      <Dialog open={!!reveal} onOpenChange={(o) => !o && setReveal(null)}>
        <DialogContent className="bg-[hsl(var(--pdg-charcoal))] border-border">
          <DialogHeader>
            <DialogTitle className="pdg-serif text-2xl">New secret &mdash; shown once</DialogTitle>
            <DialogDescription className="text-muted-foreground">Store this value in your environment and configure the counterparty now. It will not be shown again.</DialogDescription>
          </DialogHeader>
          {reveal && (
            <div className="space-y-3">
              <div className="pdg-mono text-xs text-foreground break-all bg-[hsl(var(--pdg-obsidian))] rounded-lg p-4 border border-border">{reveal.raw_value_once}</div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Fingerprint <span className="pdg-mono text-[hsl(var(--pdg-gold))]">{reveal.fingerprint}</span> &middot; length {reveal.length}</span>
                <button onClick={() => copy(reveal.raw_value_once)} className={goldBtnSm}><Copy size={13} /> Copy</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}
